import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, MicOff, Loader2, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";

const WAKE = /\b(e+h*[iy]?|hey|ehi|ei|ok)\s*,?\s*men[uùú]\b/i;
const YES = /^\s*(s[iì]|confermo|ok|va bene|certo|procedi)\b/i;
const NO = /^\s*(no|annulla|cancella|lascia stare|stop)\b/i;
const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

export function VoiceAssistant() {
  const { restaurant } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [on, setOn] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const [open, setOpen] = useState(false);
  const recRef = useRef(null);
  const stateRef = useRef({ phase: "idle", pending: null, awake: false, timer: null, on: false });
  const rid = restaurant?.id;

  const speak = useCallback((text) => new Promise((res) => {
    if (!window.speechSynthesis) return res();
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "it-IT"; u.rate = 1.05;
    const v = window.speechSynthesis.getVoices().find((x) => x.lang?.startsWith("it"));
    if (v) u.voice = v;
    u.onend = res; u.onerror = res;
    window.speechSynthesis.speak(u);
  }), []);

  const setP = (p) => { stateRef.current.phase = p; setPhase(p); };

  const send = useCallback(async (body) => {
    setP("processing"); setOpen(true);
    try {
      const { data } = await api.post(`/restaurants/${rid}/voice/command`, { session_id: "voice", ...body });
      setReply(data.speech);
      (data.actions || []).forEach((a) => { if (a.type === "navigate") navigate(a.path); if (a.type === "refresh") window.dispatchEvent(new Event("voice-refresh")); });
      if (data.needs_confirmation) { stateRef.current.pending = data.pending; setP("confirm"); await speak(data.speech); stateRef.current.awake = true; armTimeout(8000); }
      else { stateRef.current.pending = null; await speak(data.speech); setP("idle"); stateRef.current.awake = false; }
    } catch (ex) { const m = errMsg(ex); setReply(m); await speak(t("voice_error")); setP("idle"); stateRef.current.awake = false; }
  }, [rid, navigate, speak, t]);

  const armTimeout = (ms) => {
    clearTimeout(stateRef.current.timer);
    stateRef.current.timer = setTimeout(() => { if (stateRef.current.phase !== "processing") { stateRef.current.awake = false; stateRef.current.pending = null; setP("idle"); } }, ms);
  };

  const handleFinal = useCallback((text) => {
    const s = stateRef.current;
    if (s.phase === "processing") return;
    if (s.phase === "confirm" && s.pending) {
      if (YES.test(text)) { setTranscript(text); return send({ confirm: true, pending: s.pending }); }
      if (NO.test(text)) { setTranscript(text); return send({ confirm: false, pending: s.pending }); }
      return;
    }
    const m = text.match(WAKE);
    if (m) {
      const rest = text.slice(m.index + m[0].length).replace(/^[\s,.!?]+/, "").trim();
      if (rest.length > 2) { setTranscript(rest); return send({ text: rest }); }
      s.awake = true; setP("awake"); setOpen(true); setTranscript(""); setReply(t("voice_listening")); armTimeout(7000); return;
    }
    if (s.awake) { s.awake = false; setTranscript(text); return send({ text }); }
  }, [send, t]);

  const start = useCallback(() => {
    if (!SR) return;
    const rec = new SR();
    rec.lang = "it-IT"; rec.continuous = true; rec.interimResults = true;
    rec.onresult = (e) => {
      const last = e.results[e.results.length - 1];
      const text = last[0].transcript.trim();
      if (!last.isFinal) { if (stateRef.current.awake || WAKE.test(text)) { setTranscript(text.replace(WAKE, "").trim()); setOpen(true); } return; }
      handleFinal(text);
    };
    rec.onerror = (e) => { if (e.error === "not-allowed") { setOn(false); stateRef.current.on = false; setReply(t("voice_denied")); setOpen(true); } };
    rec.onend = () => { if (stateRef.current.on) setTimeout(() => { try { rec.start(); } catch { /* already started */ } }, 300); };
    recRef.current = rec;
    try { rec.start(); } catch { /* noop */ }
  }, [handleFinal, t]);

  useEffect(() => {
    if (!on) { stateRef.current.on = false; recRef.current?.stop(); return; }
    stateRef.current.on = true; start();
    return () => { stateRef.current.on = false; recRef.current?.stop(); };
  }, [on, start]);

  useEffect(() => () => window.speechSynthesis?.cancel(), []);

  const pushToTalk = () => {
    const s = stateRef.current;
    window.speechSynthesis?.cancel();
    if (!on) { setOn(true); }
    if (s.phase === "processing") return;
    s.awake = true; setP("awake"); setOpen(true); setTranscript(""); setReply(t("voice_listening")); armTimeout(8000);
  };

  const recorderRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const canRecord = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof window.MediaRecorder !== "undefined";
  const startRecording = async () => {
    if (recording || stateRef.current.phase === "processing") return;
    try {
      window.speechSynthesis?.cancel();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => window.MediaRecorder.isTypeSupported(m)) || "";
      const rec = new window.MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1500) return;
        setP("processing"); setOpen(true); setReply(t("voice_transcribing"));
        try {
          const fd = new FormData(); fd.append("file", blob, "voice." + (blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm"));
          const { data } = await api.post(`/restaurants/${rid}/voice/transcribe`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          const text = (data.text || "").trim();
          if (!text) { setReply(t("voice_error")); setP("idle"); return; }
          setP("idle");
          if (stateRef.current.pending) { stateRef.current.phase = "confirm"; handleFinal(text); return; }
          setTranscript(text); send({ text });
        } catch (ex) { setReply(errMsg(ex)); setP("idle"); }
      };
      recorderRef.current = rec; rec.start(); setRecording(true); setOpen(true); setReply(t("voice_recording"));
    } catch { setReply(t("voice_denied")); setOpen(true); }
  };
  const stopRecording = () => { const r = recorderRef.current; if (r && r.state !== "inactive") r.stop(); };

  if (!rid) return null;
  if (!restaurant.voice_enabled) return (
    <button data-testid="voice-upsell-btn" onClick={() => navigate("/app/subscription")} title={t("voice_business_only")}
      className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-500 hover:bg-slate-200"><MicOff size={14} /><span className="hidden sm:inline">{t("voice_business_badge")}</span></button>
  );
  const holdProps = { onPointerDown: (e) => { e.preventDefault(); startRecording(); }, onPointerUp: stopRecording, onPointerLeave: stopRecording, onPointerCancel: stopRecording, onContextMenu: (e) => e.preventDefault() };
  const pttTone = recording ? "bg-rose-600 animate-pulse" : phase === "awake" || phase === "confirm" ? "bg-[#5aa469] animate-pulse" : phase === "processing" ? "bg-amber-500" : "bg-slate-900 hover:bg-slate-800";
  const tone = phase === "processing" ? "bg-amber-500" : phase === "awake" || phase === "confirm" ? "bg-[#5aa469] animate-pulse" : on ? "bg-slate-900" : "bg-slate-200 text-slate-600";
  return (
    <>
      {SR ? <button data-testid="voice-toggle-btn" title={on ? t("voice_on") : t("voice_off")} onClick={() => { setOn((v) => !v); setOpen(!on); if (!on) speak(t("voice_ready")); }}
        className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-white transition-colors ${tone}`}>
        {phase === "processing" ? <Loader2 size={14} className="animate-spin" /> : on ? <Mic size={14} /> : <MicOff size={14} />}<span className="hidden sm:inline">{on ? "Ehi Menù" : t("voice_off")}</span>
      </button>
      : <button data-testid="voice-toggle-btn" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-bold text-white"><Mic size={14} /><span className="hidden sm:inline">Ehi Menù</span></button>}
      {SR ? <button data-testid="voice-ptt-btn" onClick={pushToTalk} title={t("voice_ptt")} aria-label={t("voice_ptt")}
        className={`fixed bottom-4 left-4 z-50 flex h-14 w-14 items-center justify-center rounded-full text-white shadow-2xl transition-transform active:scale-95 lg:left-auto lg:right-[400px] ${pttTone}`}>
        {phase === "processing" ? <Loader2 size={22} className="animate-spin" /> : <Mic size={22} />}
      </button>
      : canRecord && <button data-testid="voice-hold-btn" {...holdProps} title={t("voice_hold")} aria-label={t("voice_hold")}
        className={`fixed bottom-4 left-4 z-50 flex h-14 w-14 touch-none select-none items-center justify-center rounded-full text-white shadow-2xl lg:left-auto lg:right-[400px] ${pttTone}`}>
        {phase === "processing" ? <Loader2 size={22} className="animate-spin" /> : <Mic size={22} />}
      </button>}
      {open && (
        <div data-testid="voice-panel" className="fixed bottom-4 right-4 z-50 w-[min(92vw,360px)] rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-2xl backdrop-blur">
          <div className="mb-2 flex items-center justify-between">
            <p className="font-heading text-sm font-extrabold">Ehi Menù <span data-testid="voice-phase" className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{t(`voice_${phase}`)}</span></p>
            <button data-testid="voice-panel-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
          </div>
          {transcript && <p data-testid="voice-transcript" className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700">“{transcript}”</p>}
          {reply && <p data-testid="voice-reply" className="mt-2 text-sm font-semibold text-slate-900">{reply}</p>}
          {phase === "confirm" && <div className="mt-3 flex gap-2"><button data-testid="voice-confirm-yes" onClick={() => send({ confirm: true, pending: stateRef.current.pending })} className="flex-1 rounded-xl bg-[#5aa469] py-2 text-sm font-bold text-white">{t("yes")}</button><button data-testid="voice-confirm-no" onClick={() => send({ confirm: false, pending: stateRef.current.pending })} className="flex-1 rounded-xl bg-slate-100 py-2 text-sm font-bold">{t("no")}</button></div>}
          <form onSubmit={(e) => { e.preventDefault(); const v = e.target.elements.cmd.value.trim(); if (v) { setTranscript(v); stateRef.current.phase === "confirm" ? handleFinal(v) : send({ text: v }); e.target.reset(); } }} className="mt-3 flex gap-2">
            <input name="cmd" data-testid="voice-text-input" placeholder={t("voice_type_hint")} className="flex-1 rounded-xl border border-slate-200 px-3 py-1.5 text-sm" />
            <button data-testid="voice-text-send" className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs font-bold text-white">{t("send")}</button>
          </form>
          <p className="mt-2 text-[11px] text-slate-500">{SR ? `${t("voice_hint")} ${t("voice_ptt_hint")}` : t("voice_unsupported_hint")}</p>
        </div>
      )}
    </>
  );
}
